// ============================================================
// MyPayrollPage — 員工版薪資條（只看自己）
// ------------------------------------------------------------
// 設計：適合手機單手閱覽
//   - 大字實付金額放最上面
//   - 可切換月份
//   - 收入明細 + 扣除明細 分區顯示
//   - 展開看本月每日工時
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { formatTaipeiTime, shiftMonth, taipeiThisMonth } from '../lib/datetime';
import { ErrorBanner } from './ErrorBanner';

interface DailyRecord {
  date: string;
  clockIn: string | null;
  clockOut: string | null;
  status: string;
  workHours: number;
  overtimeHours: number;
}

interface HolidayDetail {
  date: string;
  holidayName: string;
  workHours: number;
  basePay: number;
  overtimePay: number;
  total: number;
}

interface AdjustmentItem {
  id: string;
  category: 'BONUS' | 'DEDUCTION' | 'OVERRIDE_LABOR_INS' | 'OVERRIDE_HEALTH_INS';
  label: string;
  amount: number;
}

interface Payroll {
  workDays: number;
  totalWorkHours: number;
  regularHours: number;
  overtimeHours: number;
  basePay: number;
  overtimePay: number;
  holidayBonus: number;
  holidayDetails: HolidayDetail[];
  adjustmentBonus: number;
  adjustmentDeduction: number;
  adjustmentItems: AdjustmentItem[];
  grossPay: number;
  lateCount: number;
  lateDeduct: number;
  leaveDeduct: number;
  laborIns: number;
  healthIns: number;
  totalDeductions: number;
  netPay: number;
  isZeroWork: boolean;
  insuranceGradeUsed: number;
  hourlyRate: number;
  monthlySalary: number;
}

interface MyPayrollData {
  month: string;
  user: {
    name: string;
    payType: 'HOURLY' | 'MONTHLY' | string;
    employmentType: string;
    hireDate: string;
  };
  payroll: Payroll;
  dailyRecords: DailyRecord[];
  leaveUsage: Record<string, number>;
}

const LEAVE_LABEL: Record<string, string> = {
  ANNUAL: '特休', SICK: '病假', PERSONAL: '事假', OFFICIAL: '公假',
  MENSTRUAL: '生理假', FAMILY_CARE: '家庭照顧假',
  MATERNITY: '產假', PATERNITY: '陪產假', UNPAID: '無薪假', BEREAVEMENT: '喪假',
};

const STATUS_COLOR: Record<string, string> = {
  NORMAL: 'text-sage-600',
  LATE: 'text-rose-500',
  EARLY_LEAVE: 'text-amber-600',
  MISSED: 'text-orange-500',
};

export function MyPayrollPage() {
  const { user } = useAuthStore();
  const [month, setMonth] = useState(taipeiThisMonth);
  const [data, setData] = useState<MyPayrollData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<MyPayrollData>(`/my-payroll?month=${month}`);
      setData(r);
      setError(null);
    } catch (err) { setError((err as Error).message); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigateMonth = (delta: number) => setMonth(m => shiftMonth(m, delta));

  const [y, m] = month.split('-').map(Number);
  const monthLabel = `${y} 年 ${m} 月`;

  return (
    <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
      {error && <ErrorBanner message={error} onRetry={fetchData} />}

      <h1 className="text-xl font-bold text-morandi-800">我的薪資條</h1>

      {/* 月份選擇 */}
      <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 shadow-card">
        <button
          onClick={() => navigateMonth(-1)}
          className="w-9 h-9 rounded-full flex items-center justify-center text-morandi-500 hover:bg-morandi-100 text-lg"
        >‹</button>
        <span className="text-base font-semibold text-morandi-700">{monthLabel}</span>
        <button
          onClick={() => navigateMonth(1)}
          className="w-9 h-9 rounded-full flex items-center justify-center text-morandi-500 hover:bg-morandi-100 text-lg"
        >›</button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : data ? (
        <>
          {/* 實付金額（最大字） */}
          <div className="card-morandi p-6 text-center ring-2 ring-sage-300">
            <div className="text-xs text-morandi-400 mb-1">本月實付</div>
            <div className="text-4xl font-bold font-mono text-sage-700">
              ${data.payroll.netPay.toLocaleString()}
            </div>
            <div className="text-xs text-morandi-400 mt-2">
              出勤 {data.payroll.workDays} 天 · 工時 {data.payroll.totalWorkHours} hr
              {data.payroll.overtimeHours > 0 && ` · 加班 ${data.payroll.overtimeHours} hr`}
            </div>
          </div>

          {/* 收入明細 */}
          <div className="card-morandi p-4 space-y-2">
            <div className="text-sm font-semibold text-morandi-700 mb-1">💰 收入明細</div>

            {data.user.payType === 'MONTHLY' ? (
              <Row label="底薪（月薪）" value={`$${data.payroll.monthlySalary.toLocaleString()}`} />
            ) : (
              <>
                {/* 用 totalWorkHours：後端 HOURLY 的 basePay = 全部工時 × 時薪。
                    原本寫 regularHours（每日 8 小時以內的部分），
                    工時超過 8 小時的日子會出現「$190 × 168hr = $37,240」
                    這種一眼就對不起來的算式。 */}
                <Row
                  label={`時薪 $${data.payroll.hourlyRate} × ${data.payroll.totalWorkHours}hr`}
                  value={`$${data.payroll.basePay.toLocaleString()}`}
                />
                {data.payroll.overtimeHours > 0 && (
                  <div className="text-xs text-morandi-400 -mt-1">
                    · 時薪制超過 8 小時的時數同樣以 ×1 計入上方金額，
                    國定假日出勤另計加倍（§39）
                  </div>
                )}
              </>
            )}

            {data.payroll.overtimePay > 0 && (
              <Row
                label={`加班費（§24 ${data.payroll.overtimeHours}hr）`}
                value={`+$${data.payroll.overtimePay.toLocaleString()}`}
                highlight
              />
            )}

            {data.payroll.holidayBonus > 0 && (
              <>
                <Row
                  label={`國定假日加倍（§39）`}
                  value={`+$${data.payroll.holidayBonus.toLocaleString()}`}
                  highlight
                />
                {data.payroll.holidayDetails.map(h => (
                  <div key={h.date + h.holidayName} className="text-xs text-morandi-400 ml-4 -mt-1">
                    · {h.date} {h.holidayName} {h.workHours}hr → ${h.total.toLocaleString()}
                  </div>
                ))}
              </>
            )}

            {data.payroll.adjustmentItems.filter(a => a.category === 'BONUS').map(a => (
              <Row key={a.id} label={a.label} value={`+$${a.amount.toLocaleString()}`} highlight />
            ))}

            <div className="border-t border-morandi-200 pt-2 mt-2">
              <Row label="應付小計" value={`$${data.payroll.grossPay.toLocaleString()}`} bold />
            </div>
          </div>

          {/* 扣除明細 */}
          <div className="card-morandi p-4 space-y-2">
            <div className="text-sm font-semibold text-morandi-700 mb-1">📤 扣除明細</div>
            <Row
              label={`勞保（投保 $${data.payroll.insuranceGradeUsed.toLocaleString()}）`}
              value={data.payroll.laborIns > 0 ? `-$${data.payroll.laborIns}` : '-'}
            />
            <Row
              label="健保"
              value={data.payroll.healthIns > 0 ? `-$${data.payroll.healthIns}` : '-'}
            />
            {data.payroll.lateDeduct > 0 && (
              <Row
                label={`遲到扣款（${data.payroll.lateCount} 次）`}
                value={`-$${data.payroll.lateDeduct}`}
                warn
              />
            )}
            {data.payroll.leaveDeduct > 0 && (
              <Row label="事假/無薪假扣薪" value={`-$${data.payroll.leaveDeduct}`} warn />
            )}
            {data.payroll.adjustmentItems.filter(a => a.category === 'DEDUCTION').map(a => (
              <Row key={a.id} label={a.label} value={`-$${a.amount.toLocaleString()}`} warn />
            ))}

            <div className="border-t border-morandi-200 pt-2 mt-2">
              <Row
                label="扣除小計"
                value={data.payroll.totalDeductions > 0 ? `-$${data.payroll.totalDeductions.toLocaleString()}` : '$0'}
                bold
              />
            </div>
          </div>

          {/* 假期使用 */}
          {Object.keys(data.leaveUsage).length > 0 && (
            <div className="card-morandi p-4 space-y-1">
              <div className="text-sm font-semibold text-morandi-700 mb-2">🏖 本月請假</div>
              {Object.entries(data.leaveUsage).map(([type, hrs]) => (
                <Row
                  key={type}
                  label={LEAVE_LABEL[type] ?? type}
                  value={`${hrs} hr（${Math.round(hrs / 8 * 10) / 10} 天）`}
                />
              ))}
            </div>
          )}

          {/* 每日打卡展開 */}
          <button
            onClick={() => setShowDetails(v => !v)}
            className="w-full card-morandi p-3 text-sm text-morandi-600 hover:bg-morandi-50 flex items-center justify-center gap-1"
          >
            {showDetails ? '收合' : '查看'}本月每日打卡 {showDetails ? '▲' : '▼'}
          </button>

          {showDetails && data.dailyRecords.length > 0 && (
            <div className="card-morandi p-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-morandi-400">
                    <th className="text-left py-2 px-1">日期</th>
                    <th className="text-center py-2 px-1">上班</th>
                    <th className="text-center py-2 px-1">下班</th>
                    <th className="text-center py-2 px-1">工時</th>
                    <th className="text-center py-2 px-1">狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {data.dailyRecords.map(r => (
                    <tr key={r.date + r.clockIn} className="border-t border-morandi-100">
                      <td className="py-2 px-1 text-morandi-600 font-mono">{r.date.slice(5)}</td>
                      <td className="text-center py-2 px-1 font-mono">{formatTaipeiTime(r.clockIn)}</td>
                      <td className="text-center py-2 px-1 font-mono">{formatTaipeiTime(r.clockOut)}</td>
                      <td className="text-center py-2 px-1 font-mono">
                        {r.workHours}
                        {r.overtimeHours > 0 && <span className="text-rose-400 text-xs">+{r.overtimeHours}</span>}
                      </td>
                      <td className={`text-center py-2 px-1 ${STATUS_COLOR[r.status] ?? ''}`}>
                        {r.status === 'NORMAL' ? '正常' :
                         r.status === 'LATE' ? '遲到' :
                         r.status === 'EARLY_LEAVE' ? '早退' :
                         r.status === 'MISSED' ? '漏卡' : r.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {showDetails && data.dailyRecords.length === 0 && (
            <div className="text-center text-morandi-300 text-sm py-4">本月無打卡紀錄</div>
          )}

          {/* 法源標註 */}
          <div className="text-xs text-morandi-300 px-2 space-y-0.5">
            <p>* §24 加班費：前 2hr 1.34×、第 3-4hr 1.67×</p>
            <p>* §39 國定假日出勤：8hr 內加發 1 倍</p>
            <p>* 勞健保費率：勞保 2.3%、健保 1.55%（勞方自付額）</p>
            <p>如對薪資有疑問，請向店長反映</p>
          </div>
        </>
      ) : (
        <div className="text-center py-10 text-morandi-300">本月無資料</div>
      )}
    </div>
  );
}

function Row({ label, value, bold, highlight, warn }: {
  label: string; value: string; bold?: boolean; highlight?: boolean; warn?: boolean;
}) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className={`text-morandi-500 ${bold ? 'font-semibold text-morandi-700' : ''}`}>{label}</span>
      <span className={`font-mono
        ${bold ? 'font-bold text-morandi-800' : ''}
        ${highlight ? 'text-amber-600 font-medium' : ''}
        ${warn ? 'text-rose-500' : ''}`}>
        {value}
      </span>
    </div>
  );
}
